/**
 * Admin — PWS × AWARE Conference Interest dashboard
 *
 * Route: /admin/pws-partner-interest
 *
 * Lists expression-of-interest submissions from the /pws-partner
 * landing page. Admins can:
 *   • filter by status
 *   • open a detail drawer with the full record + consent audit
 *   • resend the guide email
 *   • patch status + admin notes
 *   • kick off the formal Trade Portal Invite (reuses Phase 25 flow
 *     via /admin/pws-partner-interest/{id}/send-partner-invite)
 *   • export CSV
 *   • download the printable conference QR code (PNG + SVG)
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Search, Download, RefreshCw, ChevronLeft, X, Mail, Send,
  QrCode, Loader2, ExternalLink, Info, AlertTriangle, ChevronRight,
} from "lucide-react";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { useSEO } from "@/lib/useSEO";
import { Link } from "react-router-dom";

const STATUS_ORDER = [
  { key: "all",            label: "All",              tone: "bg-slate-100 text-slate-700" },
  { key: "new",            label: "New",              tone: "bg-cyan-100 text-cyan-800" },
  { key: "contacted",      label: "Contacted",        tone: "bg-blue-100 text-blue-800" },
  { key: "approved",       label: "Approved",         tone: "bg-emerald-100 text-emerald-800" },
  { key: "invite_sent",    label: "Invite sent",      tone: "bg-violet-100 text-violet-800" },
  { key: "joined",         label: "Joined",           tone: "bg-teal-100 text-teal-800" },
  { key: "not_proceeding", label: "Not proceeding",   tone: "bg-rose-100 text-rose-800" },
];

function statusTone(status) {
  return STATUS_ORDER.find((s) => s.key === status)?.tone || "bg-slate-100 text-slate-700";
}

export default function AdminPwsPartnerInterest() {
  useSEO({
    title: "PWS × AWARE Conference Interest — Admin",
    noindex: true,
    includeBaseline: false,
  });
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  // Support deep-links from the internal admin alert email:
  // /admin/pws-partner-interest?lead=<id> auto-opens the drawer.
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const requested = searchParams.get("lead");
    if (requested) setSelectedId(requested);
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = status === "all" ? {} : { status };
        const { data } = await api.get("/admin/pws-partner-interest", { params });
        if (!cancelled) setLeads(Array.isArray(data) ? data : []);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [status, reloadKey]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return leads;
    return leads.filter((l) => {
      const hay = [
        l.first_name, l.last_name, l.email, l.mobile,
        l.franchise_region, l.questions,
      ].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [leads, search]);

  async function downloadCsv() {
    try {
      const token = localStorage.getItem("scwt_token");
      const r = await fetch(`${API_BASE}/admin/pws-partner-interest/export.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "pws-partner-interest.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("CSV download failed");
    }
  }

  async function downloadQr(format /* "png" | "svg" */) {
    try {
      const token = localStorage.getItem("scwt_token");
      const r = await fetch(`${API_BASE}/admin/pws-partner-interest/qr-code.${format}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pws-partner-conference-qr.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(`QR ${format.toUpperCase()} download failed`);
    }
  }

  const selectedLead = selectedId
    ? (leads.find((l) => l.id === selectedId) || { id: selectedId })
    : null;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-7xl px-6 py-8">
        <Link
          to="/admin/partners/network"
          className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"
        >
          <ChevronLeft className="h-4 w-4" /> Back to Partner Network
        </Link>

        <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.16em] text-cyan-700 font-semibold">
              PWS × AWARE Partner Network
            </div>
            <h1 className="mt-1 text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">
              Conference Expressions of Interest
            </h1>
            <p className="mt-2 text-sm text-slate-600 max-w-2xl">
              Franchisees who submit the /pws-partner form land here for
              admin review. Sending a formal Partner Portal invite from this
              page reuses the existing Trade Portal Invite flow.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setReloadKey((k) => k + 1)}
              data-testid="pws-conference-admin-refresh"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 text-sm"
            >
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
            <button
              onClick={downloadCsv}
              data-testid="pws-conference-admin-csv"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 text-sm"
            >
              <Download className="h-4 w-4" /> Export CSV
            </button>
            <Link
              to="/pws-partner"
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 text-sm"
              data-testid="pws-conference-admin-open-landing"
            >
              <ExternalLink className="h-4 w-4" /> Open landing page
            </Link>
            <button
              onClick={async () => {
                try {
                  const token = localStorage.getItem("scwt_token");
                  const r = await fetch(`${API_BASE}/admin/pws-partner-interest/workflow.pdf`, {
                    headers: { Authorization: `Bearer ${token}` },
                  });
                  if (!r.ok) throw new Error(`HTTP ${r.status}`);
                  const blob = await r.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = "pws-aware-conference-workflow.pdf";
                  a.click();
                  URL.revokeObjectURL(url);
                } catch { toast.error("Workflow PDF download failed"); }
              }}
              data-testid="pws-conference-admin-workflow-pdf"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 text-sm"
              title="One-page admin cheat-sheet for the conference-to-portal workflow"
            >
              <Download className="h-4 w-4" /> Workflow PDF
            </button>
          </div>
        </div>

        {/* QR-code card */}
        <div
          data-testid="pws-conference-admin-qr-card"
          className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 flex flex-col md:flex-row gap-4 md:items-center md:justify-between"
        >
          <div className="flex items-start gap-3">
            <span className="inline-flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-cyan-50 text-cyan-700">
              <QrCode className="h-5 w-5" />
            </span>
            <div>
              <div className="font-semibold text-slate-900">
                Conference QR code
              </div>
              <div className="text-sm text-slate-600 mt-0.5">
                Points to <code className="text-[13px] text-slate-800">/pws-partner</code>. Print or drop into the final presentation slide.
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => downloadQr("png")}
              data-testid="pws-conference-admin-qr-png"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800"
            >
              <Download className="h-4 w-4" /> PNG
            </button>
            <button
              onClick={() => downloadQr("svg")}
              data-testid="pws-conference-admin-qr-svg"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 bg-white text-slate-700 text-sm font-medium hover:bg-slate-50"
            >
              <Download className="h-4 w-4" /> SVG
            </button>
          </div>
        </div>

        {/* Filter + search bar */}
        <div className="mt-6 flex flex-wrap items-center gap-2">
          {STATUS_ORDER.map((s) => (
            <button
              key={s.key}
              onClick={() => setStatus(s.key)}
              data-testid={`pws-conference-admin-status-${s.key}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                status === s.key ? "bg-slate-900 text-white" : `${s.tone} hover:opacity-80`
              }`}
            >
              {s.label}
            </button>
          ))}
          <div className="ml-auto relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, email, region…"
              data-testid="pws-conference-admin-search"
              className="pl-9 pr-3 py-2 rounded-lg border border-slate-300 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
          </div>
        </div>

        {/* Table */}
        <div className="mt-4 text-xs text-slate-500 flex items-center gap-1.5">
          <Info className="h-3.5 w-3.5" />
          Click any row to open the full details drawer — status, notes, resend guide, and send Partner Invite.
        </div>
        <div
          data-testid="pws-conference-admin-table"
          className="mt-2 rounded-2xl border border-slate-200 bg-white overflow-hidden"
        >
          {loading ? (
            <div className="p-10 flex items-center justify-center text-slate-500 text-sm">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-slate-500 text-sm">
              No expressions of interest match your filters yet.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs text-slate-500 uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 w-[28%]">Name / Franchise</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Mobile</th>
                  <th className="px-4 py-3">Portal?</th>
                  <th className="px-4 py-3">Guide</th>
                  <th className="px-4 py-3">Invite</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Submitted</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((l) => (
                  <tr
                    key={l.id}
                    className="group border-t border-slate-100 hover:bg-cyan-50/50 cursor-pointer transition-colors"
                    data-testid={`pws-conference-admin-row-${l.id}`}
                    onClick={() => setSelectedId(l.id)}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <ChevronRight className="h-4 w-4 flex-none text-slate-400 group-hover:text-cyan-600 transition-colors" />
                        <div>
                          <div className="font-semibold text-slate-900 group-hover:text-cyan-700 group-hover:underline">
                            {l.first_name} {l.last_name}
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5">{l.franchise_region}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{l.email}</td>
                    <td className="px-4 py-3 text-slate-700">{l.mobile}</td>
                    <td className="px-4 py-3">
                      {l.consent_portal_contact ? (
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-700 font-medium">Yes</span>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <StatusPill value={l.guide_email_status} />
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <StatusPill value={l.partner_invite_status} />
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${statusTone(l.status)}`}>
                        {l.status.replace("_", " ")}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {formatDate(l.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {selectedLead && (
        <LeadDrawer
          lead={selectedLead}
          onClose={() => {
            setSelectedId(null);
            // Clear the ?lead= query string so refreshing the page
            // doesn't reopen the same drawer unexpectedly.
            if (searchParams.get("lead")) {
              searchParams.delete("lead");
              setSearchParams(searchParams, { replace: true });
            }
          }}
          onChanged={() => setReloadKey((k) => k + 1)}
        />
      )}
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────
function StatusPill({ value }) {
  if (!value || value === "not_sent" || value === "queued") {
    return <span className="text-slate-400">—</span>;
  }
  const tone = {
    sent:      "bg-emerald-100 text-emerald-800",
    resent:    "bg-blue-100 text-blue-800",
    failed:    "bg-rose-100 text-rose-800",
    accepted:  "bg-teal-100 text-teal-800",
  }[value] || "bg-slate-100 text-slate-700";
  return (
    <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${tone}`}>
      {value}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-AU", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function LeadDrawer({ lead, onClose, onChanged }) {
  const [detail, setDetail] = useState(lead);
  const [savingStatus, setSavingStatus] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [notes, setNotes] = useState(lead.admin_notes || "");
  const [partnerId, setPartnerId] = useState("");
  const [linkExisting, setLinkExisting] = useState(false);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [resendingGuide, setResendingGuide] = useState(false);
  // Feb 2026 — one-click Create Draft PWS Partner
  const [draftBusy, setDraftBusy] = useState(false);
  const [duplicateDialog, setDuplicateDialog] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/admin/pws-partner-interest/${lead.id}`);
        setDetail(data);
        setNotes(data.admin_notes || "");
        // Auto-populate the invite input when preflight finds a matching
        // partner row — or when the draft-partner id has already been
        // stamped on this lead.
        const existing = data?.invite_preflight?.existing_partner?.id;
        const draft    = data?.draft_partner_id;
        if ((existing || draft) && !partnerId) setPartnerId(existing || draft);
      } catch {
        /* keep the row we already have */
      }
    })();
  }, [lead.id]);

  async function patchLead(patch) {
    const { data } = await api.patch(`/admin/pws-partner-interest/${lead.id}`, patch);
    setDetail(data);
    onChanged?.();
    return data;
  }

  async function saveStatus(newStatus) {
    if (newStatus === detail.status) return;
    setSavingStatus(true);
    try {
      await patchLead({ status: newStatus });
      toast.success("Status updated");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSavingStatus(false); }
  }

  async function saveNotes() {
    setSavingNotes(true);
    try {
      await patchLead({ admin_notes: notes });
      toast.success("Notes saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSavingNotes(false); }
  }

  async function resendGuide() {
    setResendingGuide(true);
    try {
      const { data } = await api.post(`/admin/pws-partner-interest/${lead.id}/resend-guide`);
      setDetail(data);
      onChanged?.();
      toast.success("Partner Guide email re-sent");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setResendingGuide(false); }
  }

  async function createDraftPartner(force = false) {
    setDraftBusy(true);
    setDuplicateDialog(null);
    try {
      const { data } = await api.post(
        `/admin/pws-partner-interest/${lead.id}/create-draft-partner`,
        { force },
      );
      // Refresh lead detail so `draft_partner_id` etc. flow into the UI.
      const { data: refreshed } = await api.get(`/admin/pws-partner-interest/${lead.id}`);
      setDetail(refreshed);
      onChanged?.();
      // Auto-populate the invite input so the "Send Partner Invite"
      // section below is one click away.
      setPartnerId(data.partner_id);
      toast.success(
        data.already_exists
          ? "Draft partner already linked — opening now."
          : "Draft PWS Partner created — opening for you to complete.",
      );
      // Open the partner in a new tab so the admin can finish the
      // ABN / postcodes / commission / slug fields.
      window.open(data.admin_partner_url, "_blank", "noopener,noreferrer");
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && (d.code === "duplicate_email" || d.code === "duplicate_business_name")) {
        setDuplicateDialog({
          code:      d.code,
          message:   d.message,
          existing:  Array.isArray(d.existing) ? d.existing : [],
          allowForce: d.code === "duplicate_business_name",
        });
      } else {
        toast.error(formatApiError(e));
      }
    } finally { setDraftBusy(false); }
  }

  async function sendInvite() {
    if (!partnerId.trim()) {
      toast.error("Enter or paste the partner id first.");
      return;
    }
    setSendingInvite(true);
    try {
      const { data } = await api.post(
        `/admin/pws-partner-interest/${lead.id}/send-partner-invite`,
        { partner_id: partnerId.trim(), link_existing_user: linkExisting },
      );
      setDetail(data);
      onChanged?.();
      toast.success("Trade Portal invite dispatched");
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = (d && d.reason) ? `${d.reason}: ${d.detail || ""}` : formatApiError(e);
      toast.error(msg);
    } finally { setSendingInvite(false); }
  }

  const pf = detail.invite_preflight || {};

  return (
    <div className="fixed inset-0 z-40 flex" data-testid="pws-conference-admin-drawer">
      <div className="absolute inset-0 bg-slate-900/50" onClick={onClose} />
      <div className="relative ml-auto w-full max-w-xl h-full bg-white shadow-2xl overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500">
              Interest — {detail.id}
            </div>
            <h2 className="text-lg font-semibold text-slate-900">
              {detail.first_name} {detail.last_name}
            </h2>
          </div>
          <button
            onClick={onClose}
            data-testid="pws-conference-admin-drawer-close"
            className="p-2 rounded-lg text-slate-500 hover:bg-slate-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <Section title="Contact">
            <Row k="Franchise / region" v={detail.franchise_region} />
            <Row k="Email" v={<a className="text-cyan-700 hover:underline" href={`mailto:${detail.email}`}>{detail.email}</a>} />
            <Row k="Mobile" v={detail.mobile} />
            <Row k="Portal contact" v={detail.consent_portal_contact ? "Yes" : "No"} />
          </Section>

          {/* Feb 2026 — one-click "Create Draft PWS Partner" from
              this lead's data. Sits above the invite section because
              creating the partner is the prerequisite step. */}
          <Section title="PWS partner record">
            {detail.draft_partner_id ? (
              <div
                data-testid="pws-conference-admin-draft-partner-created"
                className="flex items-start gap-2 text-xs bg-emerald-50 border border-emerald-200 rounded-lg p-3"
              >
                <Info className="h-4 w-4 flex-none text-emerald-700 mt-0.5" />
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-semibold text-[10px] uppercase tracking-wider">
                      Draft partner created
                    </span>
                    <span className="font-mono text-[11px] text-emerald-900/80">
                      {detail.draft_partner_id}
                    </span>
                  </div>
                  <div className="mt-1 text-emerald-900/80">
                    Created by <b>{detail.draft_partner_created_by || "admin"}</b>
                    {detail.draft_partner_created_at && (
                      <> · {formatDate(detail.draft_partner_created_at)}</>
                    )}
                  </div>
                  <Link
                    to={`/admin/partners/network?open=${detail.draft_partner_id}`}
                    target="_blank" rel="noopener noreferrer"
                    data-testid="pws-conference-admin-open-draft-partner"
                    className="mt-2 inline-flex items-center gap-1 text-emerald-700 font-medium hover:underline"
                  >
                    Open Draft Partner <ExternalLink className="h-3 w-3" />
                  </Link>
                </div>
              </div>
            ) : (
              <>
                <p className="text-xs text-slate-600 mb-3 leading-relaxed">
                  Spin up a <b>draft</b> PWS Partner row pre-filled with this
                  lead&apos;s details. The partner opens for you to complete
                  ABN, service postcodes, commission, and slug. Nothing is
                  activated, no emails sent, no login provisioned.
                </p>
                <button
                  onClick={() => createDraftPartner(false)}
                  disabled={draftBusy}
                  data-testid="pws-conference-admin-create-draft-partner"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white hover:bg-cyan-700 text-sm font-semibold disabled:opacity-50"
                >
                  {draftBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ChevronRight className="h-4 w-4" />}
                  Create Draft PWS Partner
                </button>
              </>
            )}

            {/* Duplicate warning modal — Email conflict (no override) or
                Business name conflict (admin can override). */}
            {duplicateDialog && (
              <div
                data-testid="pws-conference-admin-duplicate-dialog"
                className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs"
              >
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 flex-none text-amber-700 mt-0.5" />
                  <div className="flex-1">
                    <b className="text-amber-900">
                      {duplicateDialog.code === "duplicate_email"
                        ? "Existing partner uses this email"
                        : "Existing partner uses this business name"}
                    </b>
                    <div className="mt-0.5 text-amber-900/80">
                      {duplicateDialog.message}
                    </div>
                    <ul className="mt-2 space-y-1">
                      {duplicateDialog.existing.map((p) => (
                        <li key={p.id} className="bg-white border border-amber-200 rounded px-2 py-1.5">
                          <div className="font-semibold text-slate-800">
                            {p.business_name || "(unnamed)"}
                          </div>
                          <div className="text-[11px] text-slate-500 flex flex-wrap gap-x-2">
                            <span className="font-mono">{p.id}</span>
                            <span>{p.email}</span>
                            <span>{p.partner_type}</span>
                            <span>{p.pws_partner_status || p.status || "no status"}</span>
                          </div>
                          <Link
                            to={`/admin/partners/network?open=${p.id}`}
                            target="_blank" rel="noopener noreferrer"
                            data-testid={`pws-conference-admin-open-existing-${p.id}`}
                            className="mt-1 inline-flex items-center gap-1 text-cyan-700 font-medium hover:underline text-[11px]"
                          >
                            Open Existing <ExternalLink className="h-3 w-3" />
                          </Link>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-2 flex gap-2 flex-wrap">
                      <button
                        onClick={() => setDuplicateDialog(null)}
                        data-testid="pws-conference-admin-duplicate-cancel"
                        className="px-3 py-1.5 rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 text-[11px] font-semibold"
                      >
                        Cancel
                      </button>
                      {duplicateDialog.allowForce && (
                        <button
                          onClick={() => createDraftPartner(true)}
                          disabled={draftBusy}
                          data-testid="pws-conference-admin-duplicate-force"
                          className="px-3 py-1.5 rounded bg-amber-600 text-white hover:bg-amber-700 text-[11px] font-semibold disabled:opacity-50"
                        >
                          Create Anyway
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Section>

          {/* Feb 2026 (July) — Primary admin action lives at the top so
              it's the first thing you see after opening the drawer. */}
          <Section title="Send formal Partner Portal invite">
            <p className="text-xs text-slate-600 mb-3 leading-relaxed">
              Reuses the existing Trade Portal Invite (7-day setup token).
              We deliberately don&apos;t auto-mint partner accounts — you either
              pick an existing partner below, or create one first.
            </p>

            {pf.existing_partner ? (
              <div
                className="flex items-start gap-2 text-xs bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-3"
                data-testid="pws-conference-admin-preflight-partner"
              >
                <Info className="h-4 w-4 flex-none text-emerald-700 mt-0.5" />
                <div className="flex-1">
                  <b className="text-emerald-900">Existing partner found — auto-filled below.</b>
                  <div className="mt-0.5">
                    {pf.existing_partner.business_name} · {pf.existing_partner.partner_type} · {pf.existing_partner.pws_partner_status || "no PWS status"}
                  </div>
                  <div className="mt-1 font-mono text-[11px] text-emerald-900/80">{pf.existing_partner.id}</div>
                </div>
              </div>
            ) : (
              <div
                className="flex items-start gap-2 text-xs bg-slate-50 border border-slate-200 rounded-lg p-3 mb-3"
                data-testid="pws-conference-admin-preflight-none"
              >
                <Info className="h-4 w-4 flex-none text-slate-600 mt-0.5" />
                <div className="flex-1">
                  <b className="text-slate-800">No matching partner record yet.</b>
                  <div className="mt-0.5 text-slate-600">
                    Create the partner first with their correct type + commission split, then come back and click Send.
                  </div>
                  <Link
                    to="/admin/partners/network"
                    target="_blank" rel="noopener noreferrer"
                    className="mt-2 inline-flex items-center gap-1 text-cyan-700 font-medium hover:underline"
                    data-testid="pws-conference-admin-create-partner"
                  >
                    Open Partner Network <ExternalLink className="h-3 w-3" />
                  </Link>
                </div>
              </div>
            )}

            {pf.existing_user && (
              <div className="flex items-start gap-2 text-xs bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3" data-testid="pws-conference-admin-preflight-user">
                <AlertTriangle className="h-4 w-4 flex-none text-amber-700 mt-0.5" />
                <div>
                  <b>Existing user account:</b> role={pf.existing_user.role} · status={pf.existing_user.status}
                  {pf.existing_user.partner_id && <div>Linked to partner <span className="font-mono">{pf.existing_user.partner_id}</span></div>}
                  <div className="mt-1">
                    <label className="inline-flex items-center gap-1.5">
                      <input
                        type="checkbox"
                        checked={linkExisting}
                        onChange={(e) => setLinkExisting(e.target.checked)}
                        data-testid="pws-conference-admin-link-existing"
                        className="h-3.5 w-3.5"
                      />
                      Link the orphan user to this partner
                    </label>
                  </div>
                </div>
              </div>
            )}

            <label className="block text-[11px] text-slate-500 mb-1">Partner id to invite</label>
            <input
              type="text"
              value={partnerId}
              onChange={(e) => setPartnerId(e.target.value)}
              placeholder="pwsp_… (paste from Partner Network)"
              data-testid="pws-conference-admin-partner-id"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
            />
            <button
              onClick={sendInvite}
              disabled={sendingInvite || !partnerId.trim()}
              data-testid="pws-conference-admin-send-invite"
              className="mt-3 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-emerald-600 text-white hover:bg-emerald-500 text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {sendingInvite ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Send Partner Invite
            </button>
            {detail.partner_invite_sent_at && (
              <div
                data-testid="pws-conference-admin-invite-sent-at"
                className="mt-3 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2"
              >
                Invite already sent: {formatDate(detail.partner_invite_sent_at)}
              </div>
            )}
          </Section>

          <Section title="Status">
            <select
              value={detail.status || "new"}
              onChange={(e) => saveStatus(e.target.value)}
              disabled={savingStatus}
              data-testid="pws-conference-admin-status-select"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
            >
              {STATUS_ORDER.filter((s) => s.key !== "all").map((s) => (
                <option key={s.key} value={s.key}>{s.label}</option>
              ))}
            </select>
          </Section>

          {detail.questions && (
            <Section title="Questions / comments">
              <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-700 whitespace-pre-wrap">
                {detail.questions}
              </div>
            </Section>
          )}

          <Section title="Admin notes">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              maxLength={4000}
              data-testid="pws-conference-admin-notes"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm resize-y"
            />
            <button
              onClick={saveNotes}
              disabled={savingNotes}
              data-testid="pws-conference-admin-notes-save"
              className="mt-2 inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800 text-sm disabled:opacity-50"
            >
              {savingNotes ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Save notes
            </button>
          </Section>

          <Section title="Guide email">
            <Row k="Status" v={<StatusPill value={detail.guide_email_status} />} />
            <Row k="Sent" v={formatDate(detail.guide_email_sent_at)} />
            {detail.guide_email_last_error && (
              <Row k="Last error" v={<span className="text-rose-700 text-xs">{detail.guide_email_last_error}</span>} />
            )}
            <button
              onClick={resendGuide}
              disabled={resendingGuide}
              data-testid="pws-conference-admin-resend-guide"
              className="mt-3 inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 text-sm disabled:opacity-50"
            >
              {resendingGuide ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
              Resend guide email
            </button>
          </Section>

          <Section title="Consent captured">
            <div className="text-xs text-slate-600 space-y-1.5">
              <div><b className="text-slate-800">Guide (required):</b> {detail.consent_guide ? "Yes" : "No"} — <span className="italic">&ldquo;{detail.consent_guide_text}&rdquo;</span></div>
              <div><b className="text-slate-800">Portal contact (optional):</b> {detail.consent_portal_contact === true ? "Yes" : detail.consent_portal_contact === false ? "No" : "Not answered"} — <span className="italic">&ldquo;{detail.consent_portal_contact_text}&rdquo;</span></div>
              <div><b className="text-slate-800">Captured:</b> {formatDate(detail.consent_captured_at)} · <b>Source:</b> {detail.consent_source}</div>
            </div>
          </Section>

          <Section title="Analytics">
            <Row k="Guide opened by partner" v={formatDate(detail.partner_guide_opened_at)} />
            <Row k="PDF downloaded" v={formatDate(detail.partner_pdf_downloaded_at)} />
            <Row k="User agent" v={<span className="text-xs text-slate-500">{detail.user_agent || "—"}</span>} />
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">
        {title}
      </div>
      {children}
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-baseline gap-3 py-1.5 border-b border-slate-100 last:border-0">
      <div className="text-xs text-slate-500 min-w-[140px]">{k}</div>
      <div className="text-sm text-slate-800">{v || <span className="text-slate-400">—</span>}</div>
    </div>
  );
}
